import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import AreaDropdown from '../../project/project-tva-bosse-samleside/area-dropdown/AreaDropdown';
import MaxWidthWrapper from '../../components/max-width-wrapper/MaxWidthWrapper';
import { useMountedState } from '../../components/hooks/mountedState';
import InputSearch from '../../components/input/input-search/InputSearch';
import { fetchAllCounties, fetchAllMunicipalities, fetchAllSuburbs } from '../search-actions';
import ButtonText from '../../components/button/button-text/ButtonText';
import { SectionTitle, Subtitle } from '../../components/typography/Heading';
import Checkbox from '../../components/input/Checkbox';
import { useCategoriesWithoutOther } from '../../components/hooks/useCategories';
import Expandable from '../../components/expandable/Expandable';
import { useFeature } from '../../components/hooks/isFeatureEnabled';
import { SHOW_ADVANCED_SEARCH_OPTIONS } from '../../constants/features/constants';
import ScreenReaderOnly from '../../accessibility/ScreenReaderOnly';
import { THEMES } from '../../utils/cms-utils';
import ThemeContainer from '../../components/theme-container/ThemeContainer';
import styles from './selection-search.module.less';

const SelectionSearch = ({
    onSearch = () => {},
    onReset = () => {},
    queryMunicipality = null,
    queryCounty = null,
    querySuburb = null,
    queryText = '',
    className = '',
    showAdvancedOptions = false,
    showTextSearch = true,
    showLocationSearch = true,
    categories = null,
    availableCounties = [],
    receiverType = undefined,
    ended,
}) => {
    const baseUrl = useSelector((state) => state.baseURL);
    const [allSuburbs, setAllSuburbs] = useMountedState([]);
    const [allMunicipalities, setAllMunicipalities] = useMountedState([]);
    const [allCounties, setAllCounties] = useMountedState([]);
    const allCategories = useCategoriesWithoutOther();
    const [currentCounty, setCurrentCounty] = useMountedState(queryCounty);
    const [currentMunicipality, setCurrentMunicipality] = useMountedState(queryMunicipality);
    const [currentSuburb, setCurrentSuburb] = useMountedState(querySuburb);
    const [currentTextQuery, setCurrentTextQuery] = useMountedState(queryText);
    const [currentCategories, setCurrentCategories] = useMountedState(categories);
    const [showEnded, setShowEnded] = useMountedState(ended);
    const [currentReceiver, setCurrentReceiver] = useMountedState(receiverType);
    const showAdvancedOptionsByUser = useFeature(SHOW_ADVANCED_SEARCH_OPTIONS);

    const hasChanged =
        currentCounty !== queryCounty ||
        currentMunicipality !== queryMunicipality ||
        currentSuburb !== querySuburb ||
        currentTextQuery !== queryText ||
        currentCategories !== categories ||
        showEnded !== ended;

    const onFormSubmit = (e) => {
        e?.preventDefault();

        return onSearch({
            searchString: currentTextQuery,
            suburb: currentSuburb,
            county: currentCounty,
            municipality: currentMunicipality,
            categories: currentCategories,
            ended: showEnded,
        });
    };

    const onFilterChange = ({
        searchString = currentTextQuery,
        suburb = currentSuburb,
        county = currentCounty,
        municipality = currentMunicipality,
        categories = currentCategories,
        ended = showEnded,
        receiverType = currentReceiver,
    }) => {
        return onSearch({
            searchString,
            suburb,
            county,
            municipality,
            categories,
            ended,
            receiverType,
        });
    };

    const doReset = () => {
        setCurrentCounty(queryCounty);
        setCurrentMunicipality(queryMunicipality);
        setCurrentSuburb(querySuburb);
        setCurrentTextQuery(queryText);
        setCurrentCategories(categories);
        setShowEnded(ended);
        onReset();
    };

    useEffect(() => {
        if (showLocationSearch && !availableCounties?.length) {
            fetchAllCounties({ baseUrl }).then((counties) => setAllCounties(counties));
        } else if (showLocationSearch && availableCounties?.length > 0) {
            setAllCounties(availableCounties);
        }
    }, [baseUrl, showLocationSearch, setAllCounties]);

    useEffect(() => {
        if (currentCounty && !currentMunicipality) {
            fetchAllMunicipalities({ baseUrl, county: currentCounty }).then((municipalities) => {
                if (municipalities.length === 1) {
                    setCurrentMunicipality(municipalities[0]);
                }
                setAllMunicipalities(municipalities);
            });
        }

        if (currentMunicipality && !currentSuburb) {
            fetchAllSuburbs({ baseUrl, municipality: currentMunicipality }).then((suburbs) => setAllSuburbs(suburbs));
        }
    }, [setAllMunicipalities, currentCounty, currentMunicipality, currentSuburb]);

    const handleChangeCounty = (value) => {
        if (value === currentCounty) {
            return;
        }

        setAllMunicipalities([]);
        setAllSuburbs([]);
        setCurrentCounty(value);
        setCurrentSuburb(null);
        setCurrentMunicipality(null);
        onFilterChange({ county: value, municipality: null, suburb: null });
    };

    const handleChangeMunicipality = (value) => {
        if (value === currentMunicipality) {
            return;
        }

        setAllSuburbs([]);
        setCurrentSuburb(null);
        setCurrentMunicipality(value);
        onFilterChange({ municipality: value, suburb: null });
    };

    const handleChangeSuburb = (value) => {
        setCurrentSuburb(value);
        onFilterChange({ suburb: value });
    };

    const handleChangeShowEnded = (e) => {
        const endedValue = e.target.value;
        setShowEnded(endedValue);
        onFilterChange({ ended: endedValue });
    };

    const handleChangeShowReceiver = (e) => {
        const receiverValue = e.target.value;
        setCurrentReceiver(receiverValue);
        onFilterChange({ receiverType: receiverValue });
    };

    const handleToggleAllCategories = () => {
        const shouldToggleAll = currentCategories?.length !== allCategories?.length;
        let categoriesUpdated;
        if (shouldToggleAll) {
            categoriesUpdated = allCategories.map((c) => c.categoryId);
        } else {
            categoriesUpdated = [];
        }
        setCurrentCategories(categoriesUpdated);
        onFilterChange({ categories: categoriesUpdated });
    };

    const handleToggleCategory = (e) => {
        const shouldShowCategory = e.target.checked;
        const thisCategory = e.target.value;

        let categoriesUpdated;

        if (shouldShowCategory) {
            categoriesUpdated = [...(currentCategories ? currentCategories : []), thisCategory];
        } else {
            categoriesUpdated = currentCategories?.filter((c) => c !== thisCategory);
        }

        setCurrentCategories(categoriesUpdated);
        onFilterChange({ categories: categoriesUpdated });
    };

    return (
        <MaxWidthWrapper className={[className, styles.searchWrapper].join(' ')}>
            <div className={styles.searchContainer}>
                <form onSubmit={onFormSubmit} className={styles.formContainer}>
                    {showTextSearch && (
                        <InputSearch
                            type="search"
                            name="search"
                            value={currentTextQuery}
                            onChange={(e) => setCurrentTextQuery(e.target.value)}
                            onClickSearchIcon={onFormSubmit}
                            extraMargin={false}
                        />
                    )}
                    {showLocationSearch && allCounties.length > 0 && (
                        <div className={styles.areaSelections}>
                            <AreaDropdown
                                currentSelected={currentCounty}
                                allRegions={allCounties}
                                onChange={handleChangeCounty}
                                label={'Alle fylker'}
                            />
                            {currentCounty && currentMunicipality !== currentCounty && allMunicipalities.length > 1 && (
                                <AreaDropdown
                                    currentSelected={currentMunicipality}
                                    allRegions={allMunicipalities}
                                    onChange={handleChangeMunicipality}
                                    label={'Alle kommuner'}
                                />
                            )}
                            {currentCounty && currentMunicipality && (
                                <AreaDropdown
                                    currentSelected={currentSuburb}
                                    allRegions={allSuburbs}
                                    onChange={handleChangeSuburb}
                                    label={'Alle bydeler'}
                                />
                            )}
                        </div>
                    )}

                    {(showAdvancedOptions || showAdvancedOptionsByUser) && (
                        <Expandable className={styles.advanced} title={'Avanserte valg (beta)'}>
                            <ScreenReaderOnly>
                                <SectionTitle>Avanserte valg</SectionTitle>
                            </ScreenReaderOnly>
                            <ThemeContainer className={styles.advandedOptions} mainColor={THEMES.LIGHT_BLUE}>
                                <div>
                                    <label>
                                        <Subtitle>Vis aktive/avsluttede</Subtitle>
                                        <select onChange={handleChangeShowEnded} value={showEnded}>
                                            <option value={'all'}>Alle</option>
                                            <option value={false}>Aktive</option>
                                            <option value={true}>Avsluttede</option>
                                        </select>
                                    </label>
                                </div>
                                <div>
                                    <label>
                                        <Subtitle>Mottakere</Subtitle>
                                        <select onChange={handleChangeShowReceiver} value={currentReceiver}>
                                            <option value={'all'}>Alle</option>
                                            <option value={'organization'}>Organisasjoner</option>
                                            <option value={'user'}>Personer</option>
                                        </select>
                                    </label>
                                </div>

                                <fieldset className={styles.categories}>
                                    <div className={styles.legend}>
                                        <legend>
                                            <Subtitle className={styles.legendTitle}>Kategorier</Subtitle>
                                        </legend>
                                        <ButtonText
                                            fieldName={'all'}
                                            onClick={handleToggleAllCategories}
                                            type={'button'}
                                        >
                                            {currentCategories?.length !== allCategories?.length
                                                ? 'Velg alle'
                                                : 'Velg ingen'}
                                        </ButtonText>
                                    </div>

                                    {allCategories.map((category) => {
                                        return (
                                            <Checkbox
                                                checked={currentCategories?.includes(category.categoryId)}
                                                fieldName={category.categoryId}
                                                value={category.categoryId}
                                                key={category.categoryId}
                                                title={category.name}
                                                onChange={handleToggleCategory}
                                                wrapperClass={styles.category}
                                            />
                                        );
                                    })}
                                </fieldset>
                            </ThemeContainer>
                        </Expandable>
                    )}

                    {hasChanged && (
                        <ButtonText type="reset" onClick={doReset} className={styles.resetButton}>
                            Nullstill søket
                        </ButtonText>
                    )}
                </form>
            </div>
        </MaxWidthWrapper>
    );
};

export default SelectionSearch;
